import { Box, Text, Timeline, type TimelineItemProps } from "@mantine/core";
import type { ReactNode } from "react";

interface StepsProps {
  children?: ReactNode;
}

/**
 * Steps renders the {% steps %} tag as a numbered Mantine `Timeline`. `active` is
 * the number of steps, which reads every step as complete: `Timeline` treats
 * `active` as an index threshold, accenting item `i` when `active >= i` and the
 * line descending from it when `active - 1 >= i`. The count rather than the last
 * index is therefore what draws the final connector. Each {% step %} supplies its
 * own numbered bullet.
 */
export const Steps = ({ children }: StepsProps) => {
  const count = Array.isArray(children) ? children.length : children ? 1 : 0;

  return (
    <Timeline active={count} bulletSize={28} lineWidth={2} my="md">
      {children}
    </Timeline>
  );
};

interface StepProps extends Pick<TimelineItemProps, "__active" | "__align" | "__lineActive"> {
  children?: ReactNode;
  ordinal?: number;
  title?: string;
}

/**
 * Step renders one {% step %} as a Mantine `Timeline.Item` whose bullet shows the
 * ordinal the parent {% steps %} transform stamped. `Timeline` distributes its
 * completed state and alignment by cloning its own direct children, which in a
 * {% steps %} sequence are Steps rather than items, so Step forwards those props
 * on to the item it wraps — the accent on the bullet and on the connecting line
 * depends on them arriving there.
 */
export const Step = ({ children, ordinal, title, ...timelineProps }: StepProps) => (
  <Timeline.Item
    {...timelineProps}
    bullet={
      <Text fw={700} size="xs">
        {ordinal}
      </Text>
    }
    title={title}
  >
    <Box mt="xs">{children}</Box>
  </Timeline.Item>
);
